/**
 * Component Error Page
 *
 * Inline error fallback for component-level errors (non-critical failures).
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export interface ComponentErrorPageProps {
  error?: Error;
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export function ComponentErrorPage({
  error,
  title = 'Failed to load',
  message = 'Something went wrong loading this section.',
  onRetry,
}: ComponentErrorPageProps) {
  const theme = useTheme();
  const isDev = import.meta.env.DEV;

  return (
    <Box
      role="alert"
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.error.main, 0.05),
        border: 1,
        borderColor: alpha(theme.palette.error.main, 0.2),
        textAlign: 'center',
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {message}
      </Typography>

      {isDev && error && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontFamily: '"JetBrains Mono", monospace',
            color: 'error.main',
            mb: 2,
          }}
        >
          {error.message}
        </Typography>
      )}

      {onRetry && (
        <Button size="small" variant="outlined" color="error" onClick={onRetry}>
          Retry
        </Button>
      )}
    </Box>
  );
}


